/**
 * What you can cook at home: one dish per country (the one people there actually cook at home), each played the
 * same way (chop something to the rhythm, add things in the right order, keep the heat right), with its own
 * ingredients, its own reasons for the order, its own heat stages, and its own verdicts. Any dish can be cooked
 * anywhere; the one from where you live comes first. Ingredients cost what a market run costs there (localPrice),
 * unless you bought the dish's kit at the supermarket.
 */
import type { Country } from './travel.ts';

export type DishId = 'jollof' | 'ramen' | 'pancakes' | 'crepes' | 'dumplings';
export interface DishStep { id: string; label: string; colour: number; why: string }
export interface Dish {
  id: DishId;
  label: string;
  /** where it's home cooking */
  country: Country;
  /** ingredients for one cook (Lagos naira; localPrice elsewhere) */
  ngn: number;
  /** what you chop to the rhythm first */
  chop: { label: string; colour: number };
  steps: DishStep[];
  /** the three heat stages: caption, and the heat that's right (0..1) */
  heat: [string, number][];
  /** what the pot looks like at the end */
  finish: number;
  /** verdicts: great, good, poor, and a burnt one */
  lines: [string, string, string, string];
  hunger: number;
}

export const DISHES: Dish[] = [
  {
    id: 'jollof', label: 'Jollof rice', country: 'nigeria', ngn: 2_800, chop: { label: 'onion', colour: 0xc9a27a },
    steps: [
      { id: 'oil', label: 'Groundnut oil', colour: 0xd9a52b, why: 'Oil goes in first: everything else fries in it.' },
      { id: 'onion', label: 'Onions', colour: 0xe9dcc3, why: 'Onions go into the hot oil next, so they soften before the pepper goes in.' },
      { id: 'base', label: 'Pepper & tomato base', colour: 0xb8261b, why: "The pepper base fries in the oil and onions until it darkens. That's the flavour of jollof." },
      { id: 'season', label: 'Curry, thyme, stock cubes', colour: 0x9a7b2f, why: 'Season the fried base before the rice, so every grain takes it in.' },
      { id: 'rice', label: 'Rice', colour: 0xf2ece0, why: 'The rice goes into the seasoned base and gets stirred to coat.' },
      { id: 'stock', label: 'Stock', colour: 0x8a5a2b, why: "Stock goes in last: it's what the rice cooks in." },
    ],
    heat: [['Fry the base: high heat. Keep the needle in the green with ↑ / ↓', 0.82], ['Rice in: bring it down to medium (↓)', 0.55], ['Cover and simmer: low heat now (↓), or the bottom burns', 0.3]],
    finish: 0xc4421f, hunger: 70,
    lines: ['Proper party jollof. Smoky, red, every grain separate.', "Good jollof. Not your mother's, but good.", 'Edible. A bit soggy, a bit pale.', 'You burnt the bottom of the pot. You eat around it.'],
  },
  {
    id: 'ramen', label: 'Shoyu ramen', country: 'japan', ngn: 3_200, chop: { label: 'spring onion', colour: 0x6ab04c },
    steps: [
      { id: 'broth', label: 'Chicken broth', colour: 0xd9b46a, why: 'The broth goes in first: everything else is built on it.' },
      { id: 'tare', label: 'Shoyu tare', colour: 0x4a2a14, why: 'The tare seasons the broth: in before anything cooks in it.' },
      { id: 'noodles', label: 'Fresh noodles', colour: 0xf2e2a8, why: 'Noodles cook fast in the seasoned broth: two minutes.' },
      { id: 'chashu', label: 'Chashu pork', colour: 0xb07a52, why: 'The pork goes on top once the noodles are in the bowl.' },
      { id: 'egg', label: 'Soft-boiled egg', colour: 0xf2c94c, why: 'The egg goes on last with the toppings, halved, yolk still soft.' },
    ],
    heat: [['Bring the broth to a boil: high (↑)', 0.85], ['Noodles in: a rolling simmer (↓ a little)', 0.62], ['Keep it hot, not boiling: low (↓)', 0.35]],
    finish: 0xc89a54, hunger: 65,
    lines: ['Rich, glossy broth, springy noodles. You drink the bowl dry.', 'Good ramen. The broth could be deeper.', 'The noodles went soft and the broth is thin.', 'The broth boiled down and caught. Salty and bitter.'],
  },
  {
    id: 'pancakes', label: 'Buttermilk pancakes', country: 'usa', ngn: 2_400, chop: { label: 'strawberries', colour: 0xd63a3a },
    steps: [
      { id: 'flour', label: 'Flour, sugar, baking powder', colour: 0xf4efe4, why: 'The dry things go in the bowl first and get whisked together.' },
      { id: 'egg', label: 'Eggs', colour: 0xf2c94c, why: 'Eggs into a well in the middle of the dry mix.' },
      { id: 'milk', label: 'Buttermilk', colour: 0xf8f6f0, why: 'The buttermilk loosens it into a batter: in after the eggs.' },
      { id: 'butter', label: 'Melted butter', colour: 0xf2d27a, why: "The melted butter goes in last, so it doesn't set in cold milk." },
      { id: 'pour', label: 'Ladle onto the pan', colour: 0xe8c890, why: "Batter on the pan once it's all mixed." },
    ],
    heat: [['Heat the pan: medium-high (↑)', 0.7], ['Bubbles on top: flip, and steady (↓ a touch)', 0.55], ["Last ones: lower (↓) so they don't scorch", 0.42]],
    finish: 0xd9a052, hunger: 55,
    lines: ['A tall stack, golden, fluffy, butter melting down the sides.', 'Good pancakes. One or two came out flat.', 'Pale and a bit raw in the middle.', 'Burnt on the outside, raw inside. The smoke alarm agrees.'],
  },
  {
    id: 'crepes', label: 'Crêpes', country: 'france', ngn: 2_200, chop: { label: 'lemon', colour: 0xf2d22a },
    steps: [
      { id: 'flour', label: 'Flour and a pinch of salt', colour: 0xf4efe4, why: 'The flour goes in the bowl first.' },
      { id: 'egg', label: 'Eggs', colour: 0xf2c94c, why: 'Eggs whisked into the flour, a little at a time.' },
      { id: 'milk', label: 'Milk', colour: 0xf8f6f0, why: 'The milk thins it right down: crêpe batter is much thinner than pancake batter.' },
      { id: 'butter', label: 'Melted butter', colour: 0xf2d27a, why: 'Butter last: it stops them sticking and gives the lacy edge.' },
      { id: 'sugar', label: 'Sugar and lemon', colour: 0xf2e6a0, why: "Sugar and a squeeze of lemon on the crêpe once it's cooked." },
    ],
    heat: [['Get the pan hot: high (↑)', 0.78], ['Swirl the batter thin: keep it there', 0.7], ['Lower (↓) for the last ones', 0.5]],
    finish: 0xe8c07a, hunger: 45,
    lines: ['Paper-thin, lacy at the edges. Very French of you.', 'Good crêpes. A couple tore.', 'Thick and pale: more pancake than crêpe.', 'Scorched and stuck to the pan.'],
  },
  {
    id: 'dumplings', label: 'Pork dumplings (jiaozi)', country: 'china', ngn: 3_000, chop: { label: 'napa cabbage', colour: 0xb8d89a },
    steps: [
      { id: 'pork', label: 'Pork mince', colour: 0xd99a8a, why: "The pork goes in the bowl first: it's the filling's base." },
      { id: 'cabbage', label: 'Chopped cabbage', colour: 0xb8d89a, why: 'The cabbage you chopped goes into the meat.' },
      { id: 'season', label: 'Ginger, soy, sesame oil', colour: 0x5a3a1c, why: 'Season the filling and stir one way until it binds.' },
      { id: 'wrap', label: 'Wrappers: fold and pleat', colour: 0xf4efe4, why: 'Spoon the filling into the wrappers and pleat them shut.' },
      { id: 'water', label: 'Into the boiling water', colour: 0x9fc5e8, why: 'The dumplings go into boiling water last.' },
    ],
    heat: [['Bring the water to a boil: high (↑)', 0.85], ['They float: a splash of cold water, back down (↓)', 0.6], ['Keep a gentle boil (↓)', 0.5]],
    finish: 0xf0e6d0, hunger: 65,
    lines: ['Plump, pleated, juicy inside. Gone in minutes.', 'Good dumplings. A few split open.', 'Watery filling, wrappers too thick.', 'They stuck and tore. You eat the pieces.'],
  },
];
export const dishById = (id: string | undefined) => DISHES.find((d) => d.id === id) ?? DISHES[0];
/** The dishes, the one from where you live first. */
export const dishesFor = (country: Country) => [...DISHES].sort((a, b) => (a.country === country ? -1 : 0) - (b.country === country ? -1 : 0));
